import { type AppStateStatus } from 'react-native';
import { create } from 'zustand';
import { combine, persist, subscribeWithSelector } from 'zustand/middleware';
import { eventBus } from '../event-manager/event-bus';
import {
  updateAudioInputDeviceState,
  updateVideoInputDevice,
  updateVideoInputDeviceState,
} from '../handlers';
import type {
  AudioMode,
  CameraFacingInternal,
  Layout,
  Platform,
  WebOSName,
} from '../types/common';
import {
  CAMERA_FACING,
  CAMERA_FACING_INTERNAL,
  EVENT_LISTENER_METHODS,
  EVENT_LISTENER_METHODS_MOBILE,
  getDefaultDevice,
  LAYOUT,
  MEDIA_TYPE,
  PLATFORM,
} from '../utils';
import { getIsConferenceJoined } from './conference';
import { useConfigStore } from './config';
import { useParticipantStore } from './participants';
import { getLocalTrack } from './tracks';

export type AudioInputDevice = MediaDeviceInfo & {
  kind: Extract<MediaDeviceKind, 'audioinput'>;
};

export type AudioOutputDevice = MediaDeviceInfo & {
  kind: Extract<MediaDeviceKind, 'audiooutput'>;
};

export type VideoInputDevice = MediaDeviceInfo & {
  kind: Extract<MediaDeviceKind, 'videoinput'>;
};

export type Subscription = (() => void) | { remove: () => void };

type BaseState = {
  participantListVisible: boolean;
  moreMenuVisible: boolean;
  subscriptions: Subscription[];
  isPIPLayoutEnabled: boolean;
  audioModes: AudioMode[];
  audioInputDevices: AudioInputDevice[];
  audioOutputDevices: AudioOutputDevice[];
  videoInputDevices: VideoInputDevice[];
  audioInputDevice?: AudioInputDevice & { skipSubscribe: boolean };
  audioOutputDevice?: AudioOutputDevice;
  videoInputDevice?: VideoInputDevice & { skipSubscribe: boolean };
  previewVideoInputDevice?: VideoInputDevice;
  selectedAudioModeType?: AudioMode['type'];
  audioModeMenuVisible: boolean;
  layout: Layout;
  sidebarLayoutSetProgrammatically: boolean;
  cameraFacing: CameraFacingInternal;
  connectionRetryCount: number;
  settingsDialogVisible: boolean;
  virtualBackgroundDialogVisible: boolean;
  idealTimeoutTimer?: NodeJS.Timeout;
  idleTimeoutModalVisible: boolean;
  mirrorLocalVideo: boolean;
  isDesktopSharingEnabled: boolean;
  isBlurSupported: boolean;
  appState?: AppStateStatus;
  desktopSharingFrameRate: 5 | 15 | 30;
  chatButtonUnreadCount: number;
  enableNoiseReduction: boolean;
  sdkPlatform: Platform;
  webOSName?: WebOSName;
  previewVideoTrack?: any;
  isMobileBrowser: boolean;
  mainAreaDimension: {
    width: number;
    height: number;
  };
  tileDimensions: {
    tileWidth: number;
    tileHeight: number;
  };
  callContainerDimension: {
    width: number;
    height: number;
  };
  visibleParticipants: {
    startIndex: number;
    endIndex: number;
  };
};

type Actions = {
  toggleParticipantListVisible: () => void;
  reset: () => void;
  toggleMoreMenuVisible: () => void;
  addSubscriptions: (subscriptions: Subscription[]) => void;
  incrementConnectionRetryCount: () => void;
  clearIdealTimeoutTimer: () => void;
  restartIdealTimeoutTimer: () => void;
  isMobileSDK: () => boolean;
  isMobile: () => boolean;
};

const initialState: BaseState = {
  audioModeMenuVisible: false,
  audioModes: [],
  audioInputDevices: [],
  audioOutputDevices: [],
  videoInputDevices: [],
  cameraFacing: CAMERA_FACING_INTERNAL.USER,
  isPIPLayoutEnabled: false,
  layout: LAYOUT.TILE,
  sidebarLayoutSetProgrammatically: false,
  mainAreaDimension: {
    height: 0,
    width: 0,
  },
  tileDimensions: {
    tileHeight: 0,
    tileWidth: 0,
  },
  callContainerDimension: {
    height: 0,
    width: 0,
  },
  moreMenuVisible: false,
  participantListVisible: false,
  subscriptions: [],
  connectionRetryCount: 0,
  settingsDialogVisible: false,
  virtualBackgroundDialogVisible: false,
  idleTimeoutModalVisible: false,
  mirrorLocalVideo: true,
  isDesktopSharingEnabled: false,
  isBlurSupported: false,
  desktopSharingFrameRate: 5,
  chatButtonUnreadCount: 0,
  enableNoiseReduction: true,
  sdkPlatform: PLATFORM.WEB,
  webOSName: 'unknown',
  isMobileBrowser: false,
  visibleParticipants: {
    startIndex: 0,
    endIndex: 0,
  },
};
export const useBaseStore = create<BaseState & Actions>()(
  subscribeWithSelector(
    persist(
      combine(initialState, (set, get) => ({
        addSubscriptions: (subscriptions: Subscription[]) => {
          useBaseStore.setState(state => ({
            subscriptions: [...state.subscriptions, ...subscriptions],
          }));
        },
        reset: () =>
          set(state => ({
            ...initialState,
            // Preserve persisted properties
            audioInputDevice: state.audioInputDevice,
            audioOutputDevice: state.audioOutputDevice,
            videoInputDevice: state.videoInputDevice,
            enableNoiseReduction: state.enableNoiseReduction,
            isMobileBrowser: state.isMobileBrowser,
          })),
        toggleMoreMenuVisible: () =>
          set(state => ({
            moreMenuVisible: !state.moreMenuVisible,
          })),
        toggleParticipantListVisible: () =>
          set(state => ({
            participantListVisible: !state.participantListVisible,
          })),
        incrementConnectionRetryCount: () =>
          set(state => ({
            connectionRetryCount: state.connectionRetryCount + 1,
          })),
        isMobileSDK: () => {
          const isMobileSDK =
            get().sdkPlatform === 'android' || get().sdkPlatform === 'ios';
          return isMobileSDK;
        },
        isMobile: () => {
          const isMobileSDK =
            get().sdkPlatform === 'android' || get().sdkPlatform === 'ios';
          const isMobileBrowser = get().isMobileBrowser;
          return isMobileSDK || isMobileBrowser;
        },
        clearIdealTimeoutTimer: () => {
          const timer = get().idealTimeoutTimer;
          if (timer) {
            clearTimeout(timer);
            set({
              idealTimeoutTimer: undefined,
              idleTimeoutModalVisible: false,
            });
          }
        },
        restartIdealTimeoutTimer: () => {
          useBaseStore.getState().clearIdealTimeoutTimer();
          const iAmRecorder = useConfigStore.getState().iAmRecorder;
          if (iAmRecorder) {
            return;
          }
          const participantsCount =
            useParticipantStore.getState().participants.length;
          if (participantsCount > 0) {
            return;
          }
          const isConferenceJoined = getIsConferenceJoined();
          if (!isConferenceJoined) {
            return;
          }
          const idleTimeoutPeriodBeforePrompt =
            useConfigStore.getState().idleTimeoutPeriodBeforePrompt;
          const timer = setTimeout(() => {
            set({
              idleTimeoutModalVisible: true,
            });
          }, idleTimeoutPeriodBeforePrompt);
          set({ idealTimeoutTimer: timer });
        },
      })),
      {
        name: 'base-store',
        partialize: state => ({
          audioInputDevice: state.audioInputDevice,
          audioOutputDevice: state.audioOutputDevice,
          videoInputDevice: state.videoInputDevice,
          enableNoiseReduction: state.enableNoiseReduction,
        }),
      },
    ),
  ),
);

export const toggleParticipantListVisible =
  useBaseStore.getState().toggleParticipantListVisible;

export const hideParticipantList = () =>
  useBaseStore.setState({ participantListVisible: false });

export const showParticipantList = () =>
  useBaseStore.setState({ participantListVisible: true });

export const toggleParticipantList = () =>
  useBaseStore.setState(state => ({
    participantListVisible: !state.participantListVisible,
  }));

export const toggleMoreMenuVisible =
  useBaseStore.getState().toggleMoreMenuVisible;

export const toggleAudioModeMenuVisible = () => {
  useBaseStore.setState(state => ({
    audioModeMenuVisible: !state.audioModeMenuVisible,
  }));
};

export const setLayout = (
  layout: Layout,
  sidebarLayoutSetProgrammatically = false,
) => {
  if (layout !== LAYOUT.SIDEBAR) {
    useParticipantStore.setState({ pinnedParticipant: undefined });
  }
  useBaseStore.setState({ layout, sidebarLayoutSetProgrammatically });
};

export const hideAllBottomSheets = () => {
  useBaseStore.setState({
    audioModeMenuVisible: false,
    moreMenuVisible: false,
  });
};

export const enablePictureInPictureLayout = () =>
  useBaseStore.setState({ isPIPLayoutEnabled: true });
export const disablePictureInPictureLayout = () =>
  useBaseStore.setState({ isPIPLayoutEnabled: false });

export const showSettingsDialog = () => {
  useBaseStore.setState({ settingsDialogVisible: true });
};
export const hideSettingsDialog = () => {
  useBaseStore.setState({ settingsDialogVisible: false });
};
export const toggleSettingsDialog = () => {
  useBaseStore.setState(state => ({
    settingsDialogVisible: !state.settingsDialogVisible,
  }));
};

export const showVirtualBackgroundDialog = () => {
  useBaseStore.setState({
    virtualBackgroundDialogVisible: true,
    participantListVisible: false,
  });
};
export const hideVirtualBackgroundDialog = () => {
  useBaseStore.setState({ virtualBackgroundDialogVisible: false });
};

export const toggleMirrorLocalVideo = () => {
  useBaseStore.setState(state => ({
    mirrorLocalVideo: !state.mirrorLocalVideo,
  }));
};
export const toggleEnableNoiseReduction = () => {
  useBaseStore.setState(state => ({
    enableNoiseReduction: !state.enableNoiseReduction,
  }));
};

export const setChatButtonUnreadCount = (count: number) => {
  useBaseStore.setState({ chatButtonUnreadCount: count });
};

export const getLayout = () => {
  return useBaseStore.getState().layout;
};

export function initBaseStoreSubscriptions() {
  const unsubscribeFunctions: (() => void)[] = [];

  const unsubscribeAudioModes = useBaseStore.subscribe(
    state => state.audioModes,
    audioModes => {
      if (audioModes.length > 0) {
        const selectedAudioMode =
          audioModes.find(mode => mode.selected) ?? audioModes[0];
        useBaseStore.setState({
          selectedAudioModeType: selectedAudioMode.type,
        });
      }
    },
  );
  unsubscribeFunctions.push(unsubscribeAudioModes);

  const unsubscribeSelectedAudioModeType = useBaseStore.subscribe(
    state => state.selectedAudioModeType,
    selectedAudioModeType => {
      if (selectedAudioModeType) {
        eventBus.publish({
          payload: selectedAudioModeType,
          type: EVENT_LISTENER_METHODS_MOBILE.MediaEventsListener
            .onAudioModeChanged,
        });
      }
    },
  );
  unsubscribeFunctions.push(unsubscribeSelectedAudioModeType);

  const unsubscribeLayout = useBaseStore.subscribe(
    state => state.layout,
    layout => {
      eventBus.publish({
        payload: layout,
        type: EVENT_LISTENER_METHODS.CallLayoutListener.onCallLayoutChanged,
      });
    },
  );
  unsubscribeFunctions.push(unsubscribeLayout);

  const unsubscribeParticipantListVisible = useBaseStore.subscribe(
    state => state.participantListVisible,
    participantListVisible => {
      if (participantListVisible) {
        eventBus.publish({
          type: EVENT_LISTENER_METHODS_MOBILE.CallLayoutListener
            .onParticipantListVisible,
        });
      } else {
        eventBus.publish({
          type: EVENT_LISTENER_METHODS_MOBILE.CallLayoutListener
            .onParticipantListHidden,
        });
      }
    },
  );
  unsubscribeFunctions.push(unsubscribeParticipantListVisible);

  const unsubscribePIPLayout = useBaseStore.subscribe(
    state => state.isPIPLayoutEnabled,
    isPIPLayoutEnabled => {
      if (isPIPLayoutEnabled) {
        eventBus.publish({
          type: EVENT_LISTENER_METHODS_MOBILE.CallLayoutListener
            .onPictureInPictureLayoutEnabled,
        });
      } else {
        eventBus.publish({
          type: EVENT_LISTENER_METHODS_MOBILE.CallLayoutListener
            .onPictureInPictureLayoutDisabled,
        });
      }
    },
  );
  unsubscribeFunctions.push(unsubscribePIPLayout);

  const unsubscribeCameraFacing = useBaseStore.subscribe(
    state => state.cameraFacing,
    cameraFacing => {
      eventBus.publish({
        payload:
          cameraFacing === CAMERA_FACING_INTERNAL.USER
            ? CAMERA_FACING.FRONT
            : CAMERA_FACING.REAR,
        type: EVENT_LISTENER_METHODS_MOBILE.MediaEventsListener
          .onCameraFacingChanged,
      });
      const videoTrack = getLocalTrack(MEDIA_TYPE.VIDEO)?.originalTrack;
      if (videoTrack) {
        const currentCameraFacing = videoTrack.getCameraFacingMode();
        if (currentCameraFacing !== cameraFacing) {
          const isMobileBrowser = useBaseStore.getState().isMobileBrowser;
          if (isMobileBrowser) {
            updateVideoInputDevice(null, cameraFacing);
          } else {
            videoTrack._switchCamera();
          }
        }
      }
    },
  );
  unsubscribeFunctions.push(unsubscribeCameraFacing);

  const unsubscribeAudioInputDevices = useBaseStore.subscribe(
    state => state.audioInputDevices,
    audioInputDevices => {
      if (audioInputDevices.length > 0) {
        const audioInputDevice = useBaseStore.getState().audioInputDevice;
        if (audioInputDevice) {
          const deviceExists = audioInputDevices.some(
            device =>
              device.deviceId === audioInputDevice.deviceId &&
              device.label === audioInputDevice.label,
          );
          if (!deviceExists) {
            updateAudioInputDeviceState(getDefaultDevice(audioInputDevices));
          }
        } else {
          updateAudioInputDeviceState(
            getDefaultDevice(audioInputDevices),
            true,
          );
        }
      }
    },
  );
  unsubscribeFunctions.push(unsubscribeAudioInputDevices);

  const unsubscribeAudioOutputDevices = useBaseStore.subscribe(
    state => state.audioOutputDevices,
    audioOutputDevices => {
      if (audioOutputDevices.length > 0) {
        const audioOutputDevice = useBaseStore.getState().audioOutputDevice;
        if (!audioOutputDevice) {
          useBaseStore.setState({
            audioOutputDevice: getDefaultDevice(audioOutputDevices),
          });
        } else {
          const deviceExists = audioOutputDevices.some(
            device =>
              device.deviceId === audioOutputDevice.deviceId &&
              device.label === audioOutputDevice.label,
          );
          if (!deviceExists) {
            useBaseStore.setState({
              audioOutputDevice: getDefaultDevice(audioOutputDevices),
            });
          }
        }
      }
    },
  );
  unsubscribeFunctions.push(unsubscribeAudioOutputDevices);

  const unsubscribeVideoInputDevices = useBaseStore.subscribe(
    state => state.videoInputDevices,
    videoInputDevices => {
      if (videoInputDevices.length > 0) {
        const videoInputDevice = useBaseStore.getState().videoInputDevice;
        if (videoInputDevice) {
          const deviceExists = videoInputDevices.some(
            device =>
              device.deviceId === videoInputDevice.deviceId &&
              device.label === videoInputDevice.label,
          );
          if (!deviceExists) {
            updateVideoInputDeviceState(getDefaultDevice(videoInputDevices));
          }
        } else {
          updateVideoInputDeviceState(
            getDefaultDevice(videoInputDevices),
            true,
          );
        }
      }
    },
  );
  unsubscribeFunctions.push(unsubscribeVideoInputDevices);

  useBaseStore.subscribe(
    state => state.previewVideoTrack,
    (previewVideoTrack, prevPreviewVideoTrack) => {
      console.log('Preview video track updated:', {
        previewVideoTrack,
        prevPreviewVideoTrack,
      });
      if (prevPreviewVideoTrack) {
        if (prevPreviewVideoTrack.disposed !== true) {
          prevPreviewVideoTrack.dispose();
        }
      }
    },
  );

  useBaseStore.getState().addSubscriptions(unsubscribeFunctions);
}

initBaseStoreSubscriptions();
